import { useState, useEffect, useCallback } from 'react';
import { CONSTANTS } from '../data.js';
import { combinePair, getCardStats, eraIndexByKey } from '../lib/utils';
import type { CardInstance, EraUnlock, TutorialState } from '../types';

type GamePhase = 'normal' | 'boss_intro' | 'boss_playing' | 'boss_round_end' | 'boss_victory' | 'boss_defeat';
type RoundResult = 'player' | 'boss' | 'tie';
type Turn = 'player' | 'boss';

const shuffle = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

interface UseBossBattleProps {
  discovered: string[];
  unlockedEras: string[];
  currentEraKey: string;
  eraToAdvanceTo: EraUnlock | null;
  playerDeck: string[];
  advanceToEra: (eraKey: string) => void;
  playSoundEffect: (key: string) => void;
  cancelAdvanceAndReturnToMainMenu: () => void;
  isDeckBuilderForced: boolean;
  completedTutorials: string[];
  tutorialState: TutorialState;
  startTutorial: (key: string) => void;
  advanceTutorial: () => void;
}

export const useBossBattle = ({
  discovered,
  unlockedEras,
  currentEraKey,
  eraToAdvanceTo,
  playerDeck,
  advanceToEra,
  playSoundEffect,
  cancelAdvanceAndReturnToMainMenu,
  isDeckBuilderForced,
  completedTutorials,
  tutorialState,
  startTutorial,
  advanceTutorial,
}: UseBossBattleProps) => {
  const [gamePhase, setGamePhase] = useState<GamePhase>('normal');
  const [battlePlayerDeck, setBattlePlayerDeck] = useState<string[]>([]);
  const [bossDeck, setBossDeck] = useState<string[]>([]);
  const [playerHand, setPlayerHand] = useState<CardInstance[]>([]);
  const [bossHand, setBossHand] = useState<CardInstance[]>([]);
  const [playerFieldCards, setPlayerFieldCards] = useState<(CardInstance | null)[]>([]);
  const [bossFieldCards, setBossFieldCards] = useState<(CardInstance | null)[]>([]);
  const [playerScore, setPlayerScore] = useState(0);
  const [bossScore, setBossScore] = useState(0);
  const [roundResult, setRoundResult] = useState<RoundResult | null>(null);
  const [statusText, setStatusText] = useState("");
  const [currentBoss, setCurrentBoss] = useState<any>(null);
  const [currentTurn, setCurrentTurn] = useState<Turn>('player');
  const [playerPassed, setPlayerPassed] = useState(false);
  const [bossPassed, setBossPassed] = useState(false);
  const [dialogueIndex, setDialogueIndex] = useState(0);

  const resetBossBattle = useCallback(() => {
    setGamePhase('normal');
    setBattlePlayerDeck([]);
    setBossDeck([]);
    setPlayerHand([]);
    setBossHand([]);
    setPlayerFieldCards([]);
    setBossFieldCards([]);
    setPlayerScore(0);
    setBossScore(0);
    setRoundResult(null);
    setStatusText("");
    setCurrentBoss(null);
    setCurrentTurn('player');
    setPlayerPassed(false);
    setBossPassed(false);
    setDialogueIndex(0);
  }, []);

  const initializeBossBattle = useCallback((boss: any, prevEra: any) => {
    const bossEraKey = prevEra.key;
    const bossEraIndex = eraIndexByKey[bossEraKey];

    let pDeck: string[];
    const deckRules = CONSTANTS.deckBuilding;

    if (playerDeck && playerDeck.length >= deckRules.deckSizeMin && playerDeck.length <= deckRules.deckSizeMax) {
      pDeck = shuffle([...playerDeck]);
    } else {
      // Fallback to auto-generating a deck
      const priorityCards = Object.keys(CONSTANTS.elements).filter(k => {
          const el = CONSTANTS.elements[k];
          return el.era === bossEraKey && discovered.includes(k);
      });

      const fodderCards = Object.keys(CONSTANTS.elements).filter(k => {
          const el = CONSTANTS.elements[k];
          const elEraIndex = eraIndexByKey[el.era];
          return elEraIndex < bossEraIndex && discovered.includes(k);
      });

      if (priorityCards.length === 0) {
          priorityCards.push(...CONSTANTS.startingElements.filter(k => discovered.includes(k)));
          if (priorityCards.length === 0) priorityCards.push('stone', 'fire');
      }
      if (fodderCards.length === 0) {
          fodderCards.push(...CONSTANTS.startingElements.filter(k => discovered.includes(k)));
          if (fodderCards.length === 0) fodderCards.push('water', 'air');
      }

      const deckSize = CONSTANTS.cardGame.deckSize || 30;
      const composition = CONSTANTS.cardGame.bossBattleDeckComposition || { priorityEraRatio: 0.7, otherErasRatio: 0.3 };
      
      const numPriorityCards = Math.round(deckSize * composition.priorityEraRatio);
      const numFodderCards = deckSize - numPriorityCards;

      const generatedDeck = [];
      for (let i = 0; i < numPriorityCards; i++) {
          generatedDeck.push(priorityCards[Math.floor(Math.random() * priorityCards.length)]);
      }
      for (let i = 0; i < numFodderCards; i++) {
          generatedDeck.push(fodderCards[Math.floor(Math.random() * fodderCards.length)]);
      }
      pDeck = shuffle(generatedDeck);
    }

    const bossCardPool = boss.deck;
    const bDeck = [];
    for (let i = 0; i < (CONSTANTS.cardGame.deckSize || 30); i++) {
        bDeck.push(bossCardPool[Math.floor(Math.random() * bossCardPool.length)]);
    }
    shuffle(bDeck);

    setBattlePlayerDeck(pDeck);
    setBossDeck(bDeck);

    const handSize = CONSTANTS.cardGame.handSize || 10;
    const initialPlayerHand = pDeck.slice(0, handSize).map((key, i) => ({ id: `p_${key}_${Date.now()}_${i}_${Math.random()}`, elementKey: key }));
    const initialBossHand = bDeck.slice(0, handSize).map((key, i) => ({ id: `b_${key}_${Date.now()}_${i}_${Math.random()}`, elementKey: key }));

    setPlayerHand(initialPlayerHand);
    setBossHand(initialBossHand);
    setBattlePlayerDeck(pDeck.slice(handSize));
    setBossDeck(bDeck.slice(handSize));
    setPlayerScore(0);
    setBossScore(0);
    setPlayerFieldCards(Array(CONSTANTS.cardGame.fieldSlots).fill(null));
    setBossFieldCards(Array(CONSTANTS.cardGame.fieldSlots).fill(null));
    setRoundResult(null);
    setCurrentTurn('player');
    setPlayerPassed(false);
    setBossPassed(false);
  }, [discovered, playerDeck]);

  useEffect(() => {
    if (eraToAdvanceTo && gamePhase === 'normal' && !isDeckBuilderForced) {
      const eraIndex = CONSTANTS.eras.list.findIndex(e => e.key === eraToAdvanceTo.key);
      const prevEra = eraIndex > 0 ? CONSTANTS.eras.list[eraIndex - 1] : null;
      const boss = prevEra ? CONSTANTS.bosses[prevEra.key] : null;
      
      if (boss) {
        setCurrentBoss({ ...boss, key: prevEra!.key });
        initializeBossBattle(boss, prevEra);
        setGamePhase('boss_intro');
        setDialogueIndex(0);
        setStatusText(`${boss.name} challenges you!`);
      } else {
        advanceToEra(eraToAdvanceTo.key);
      }
    }
  }, [eraToAdvanceTo, gamePhase, advanceToEra, initializeBossBattle, isDeckBuilderForced]);

  // Re-initialize boss battle when player deck changes and we have an era to advance to
  useEffect(() => {
    if (eraToAdvanceTo && gamePhase === 'normal' && !isDeckBuilderForced && playerDeck.length > 0) {
      const eraIndex = CONSTANTS.eras.list.findIndex(e => e.key === eraToAdvanceTo.key);
      const prevEra = eraIndex > 0 ? CONSTANTS.eras.list[eraIndex - 1] : null;
      const boss = prevEra ? CONSTANTS.bosses[prevEra.key] : null;
      
      if (boss) {
        setCurrentBoss({ ...boss, key: prevEra!.key });
        initializeBossBattle(boss, prevEra);
        setGamePhase('boss_intro');
        setDialogueIndex(0);
        setStatusText(`${boss.name} challenges you!`);
      }
    }
  }, [playerDeck, eraToAdvanceTo, gamePhase, isDeckBuilderForced, initializeBossBattle]);

  const handlePlayCardOnField = useCallback((handCardIndex: number, fieldSlotIndex: number) => {
    if (currentTurn !== 'player' || playerPassed || gamePhase !== 'boss_playing') return;
    if (playerFieldCards[fieldSlotIndex] !== null) return;

    const playedCard = playerHand[handCardIndex];
    const newField = [...playerFieldCards];
    newField[fieldSlotIndex] = playedCard;
    setPlayerFieldCards(newField);

    setPlayerHand(prev => prev.filter((_, i) => i !== handCardIndex));
    playSoundEffect('play_card');
    setStatusText(`You played ${CONSTANTS.elements[playedCard.elementKey].name}.`);
    setCurrentTurn('boss');

    if (tutorialState.active === 'first_boss_battle' && tutorialState.step === 0) {
      advanceTutorial();
    }
  }, [playerHand, playerFieldCards, playSoundEffect, gamePhase, currentTurn, playerPassed, tutorialState, advanceTutorial]);

  const handleCombineOnField = useCallback((handCardIndex: number, fieldSlotIndex: number) => {
      if (currentTurn !== 'player' || playerPassed || gamePhase !== 'boss_playing') return;
      
      const fieldCard = playerFieldCards[fieldSlotIndex];
      if (!fieldCard) return;

      const handCard = playerHand[handCardIndex];
      const resultKey = combinePair(handCard.elementKey, fieldCard.elementKey);

      if (resultKey) {
          const resultElement = CONSTANTS.elements[resultKey];
          const resultEraIndex = eraIndexByKey[resultElement.era];
          const currentEraIndex = eraIndexByKey[currentEraKey];
          const isFuture = resultEraIndex > currentEraIndex;

          const newCard: CardInstance = { 
              id: `p_${resultKey}_${Date.now()}_${Math.random()}`, 
              elementKey: resultKey,
              isFuture: isFuture,
              componentCards: [handCard, fieldCard]
          };

          const newField = [...playerFieldCards];
          newField[fieldSlotIndex] = newCard;
          setPlayerFieldCards(newField);

          setPlayerHand(prev => prev.filter((_, i) => i !== handCardIndex));
          playSoundEffect('combine_success');
          setStatusText(`You created ${resultElement.name}!${isFuture ? ' (Future Time bonus!)' : ''}`);
          
          setCurrentTurn('boss');

          if (tutorialState.active === 'first_boss_battle' && tutorialState.step === 1) {
            advanceTutorial();
          }
      } else {
          playSoundEffect('combine_fail');
          setStatusText(`Cannot combine ${CONSTANTS.elements[handCard.elementKey].name} and ${CONSTANTS.elements[fieldCard.elementKey].name}.`);
      }
  }, [playerHand, playerFieldCards, playSoundEffect, gamePhase, currentTurn, playerPassed, currentEraKey, tutorialState, advanceTutorial]);

  const handlePassTurn = useCallback(() => {
    if (currentTurn !== 'player' || playerPassed || gamePhase !== 'boss_playing') return;
    setPlayerPassed(true);
    setStatusText("You passed your turn.");
    setCurrentTurn('boss');

    if (tutorialState.active === 'first_boss_battle' && tutorialState.step === 2) {
      advanceTutorial();
    }
  }, [currentTurn, playerPassed, gamePhase, tutorialState, advanceTutorial]);

  const endRound = useCallback(() => {
    const playerPower = playerFieldCards.filter(Boolean).reduce((sum, card) => sum + getCardStats(card!, currentEraKey).power, 0);
    const bossPower = bossFieldCards.filter(Boolean).reduce((sum, card) => sum + getCardStats(card!, currentEraKey).power, 0);

    let result: RoundResult;
    if (playerPower > bossPower) {
      result = 'player';
      setPlayerScore(s => s + 1);
      setStatusText(`You win the round! (${playerPower} vs ${bossPower})`);
      playSoundEffect('win_round');
    } else if (bossPower > playerPower) {
      result = 'boss';
      setBossScore(s => s + 1);
      setStatusText(`${currentBoss.name} wins the round! (${playerPower} vs ${bossPower})`);
      playSoundEffect('lose_round');
    } else {
      result = 'tie';
      setStatusText(`It's a tie! (${playerPower} vs ${bossPower})`);
    }
    setRoundResult(result);
    setGamePhase('boss_round_end');
  }, [playerFieldCards, bossFieldCards, currentBoss, playSoundEffect, currentEraKey]);

  const executeBossTurn = useCallback(() => {
    const emptySlots = bossFieldCards.filter(c => c === null).length;
    if (bossPassed || emptySlots === 0) {
      setBossPassed(true);
      if (!playerPassed) {
        setCurrentTurn('player');
      }
      return;
    }

    const playerPower = playerFieldCards.filter(Boolean).reduce((sum, card) => sum + getCardStats(card!, currentEraKey).power, 0);
    const bossPower = bossFieldCards.filter(Boolean).reduce((sum, card) => sum + getCardStats(card!, currentEraKey).power, 0);

    if (playerPassed && bossPower > playerPower) {
      setBossPassed(true);
      setStatusText(`${currentBoss.name} passes.`);
      return;
    }

    const sortedBossHand = [...bossHand].sort((a, b) => getCardStats(b, currentEraKey).power - getCardStats(a, currentEraKey).power);
    
    if (sortedBossHand.length > 0) {
      const cardToPlay = sortedBossHand[0];
      const firstEmptySlotIndex = bossFieldCards.findIndex(c => c === null);

      if (firstEmptySlotIndex !== -1) {
        const newField = [...bossFieldCards];
        newField[firstEmptySlotIndex] = cardToPlay;
        setBossFieldCards(newField);
        setBossHand(prev => prev.filter(c => c.id !== cardToPlay.id));
        setStatusText(`${currentBoss.name} played ${CONSTANTS.elements[cardToPlay.elementKey].name}.`);
      } else {
        setBossPassed(true);
      }

      if (!playerPassed) {
        setCurrentTurn('player');
      }
    } else {
      setBossPassed(true);
      setStatusText(`${currentBoss.name} is out of cards and passes.`);
      if (!playerPassed) {
        setCurrentTurn('player');
      }
    }
  }, [bossHand, bossFieldCards, playerFieldCards, bossPassed, playerPassed, currentBoss, currentEraKey]);

  useEffect(() => {
    if (gamePhase !== 'boss_playing') return;

    const playerFieldFull = playerFieldCards.every(c => c !== null);
    const bossFieldFull = bossFieldCards.every(c => c !== null);

    const bothPassed = playerPassed && bossPassed;
    const bothFull = playerFieldFull && bossFieldFull;
    const onePassedAndOtherFull = (playerPassed && bossFieldFull) || (bossPassed && playerFieldFull);

    if (bothPassed || bothFull || onePassedAndOtherFull) {
      endRound();
      return;
    }

    if (currentTurn === 'boss' && !bossPassed) {
      setTimeout(() => {
        executeBossTurn();
      }, 1000);
    } else if (currentTurn === 'boss' && bossPassed && !playerPassed) {
      setCurrentTurn('player');
    }
  }, [currentTurn, gamePhase, playerPassed, bossPassed, playerFieldCards, bossFieldCards, executeBossTurn, endRound]);

  const handleNextRound = useCallback(() => {
    if (playerScore >= CONSTANTS.cardGame.playerRoundsToWin || bossScore >= CONSTANTS.cardGame.bossRoundsToWin) {
      if (playerScore >= bossScore) {
        setGamePhase('boss_victory');
        setDialogueIndex(0);
        setStatusText("Victory! You have defeated the guardian!");
      } else {
        setGamePhase('boss_defeat');
        setDialogueIndex(0);
        setStatusText("Defeat! The guardian stands strong.");
      }
      return;
    }

    setPlayerFieldCards(Array(CONSTANTS.cardGame.fieldSlots).fill(null));
    setBossFieldCards(Array(CONSTANTS.cardGame.fieldSlots).fill(null));
    setRoundResult(null);
    setPlayerPassed(false);
    setBossPassed(false);

    const cardsToDraw = CONSTANTS.cardGame.cardsPerRound;
    const newPlayerHand = [...playerHand];
    const newBossHand = [...bossHand];
    
    const playerDraw = battlePlayerDeck.slice(0, cardsToDraw);
    playerDraw.forEach(key => newPlayerHand.push({ id: `p_${key}_${Date.now()}_${Math.random()}`, elementKey: key }));
    setBattlePlayerDeck(d => d.slice(cardsToDraw));

    const bossDraw = bossDeck.slice(0, cardsToDraw);
    bossDraw.forEach(key => newBossHand.push({ id: `b_${key}_${Date.now()}_${Math.random()}`, elementKey: key }));
    setBossDeck(d => d.slice(cardsToDraw));

    setPlayerHand(newPlayerHand);
    setBossHand(newBossHand);

    setCurrentTurn('player');
    setGamePhase('boss_playing');
    setStatusText("Your turn. Play a card or pass.");
  }, [playerScore, bossScore, playerHand, bossHand, battlePlayerDeck, bossDeck]);

  const handleBossBattleClose = useCallback(() => {
    if (playerScore >= CONSTANTS.cardGame.playerRoundsToWin && eraToAdvanceTo) {
      advanceToEra(eraToAdvanceTo.key);
    }
    setGamePhase('normal');
    setCurrentBoss(null);
    setStatusText("");
  }, [playerScore, eraToAdvanceTo, advanceToEra]);

  const startBossBattle = useCallback(() => {
    setGamePhase('boss_playing');
    setStatusText("Your turn. Play a card or pass.");
    if (currentBoss?.key === 'stone_age' && !completedTutorials.includes('first_boss_battle')) {
      startTutorial('first_boss_battle');
    }
  }, [currentBoss, completedTutorials, startTutorial]);

  const handleNextDialogue = useCallback(() => {
    setDialogueIndex(prev => prev + 1);
  }, []);

  const handleRetryBattle = useCallback(() => {
    if (currentBoss) {
      const eraIndex = CONSTANTS.eras.list.findIndex(e => e.key === currentBoss.key);
      const prevEra = eraIndex >= 0 ? CONSTANTS.eras.list[eraIndex] : null;
      initializeBossBattle(currentBoss, prevEra);
      setGamePhase('boss_playing');
      setStatusText("The battle begins anew!");
    }
  }, [currentBoss, initializeBossBattle]);

  const handleReturnToMainMenuFromDefeat = useCallback(() => {
    setGamePhase('normal');
    setCurrentBoss(null);
    setStatusText("");
    cancelAdvanceAndReturnToMainMenu();
  }, [cancelAdvanceAndReturnToMainMenu]);

  const handleOpenDeckBuilderFromDefeat = useCallback(() => {
    // This will be handled by the parent component (App.tsx)
    // We just need to provide this callback
    return 'open_deck_builder_from_defeat';
  }, []);

  return {
    gamePhase,
    playerHand,
    bossHand,
    playerFieldCards,
    bossFieldCards,
    playerScore,
    bossScore,
    roundResult,
    statusText,
    currentBoss,
    currentTurn,
    playerPassed,
    bossPassed,
    dialogueIndex,
    battlePlayerDeck,
    handlePlayCardOnField,
    handleCombineOnField,
    handlePassTurn,
    handleNextRound,
    handleBossBattleClose,
    startBossBattle,
    handleNextDialogue,
    handleRetryBattle,
    handleReturnToMainMenuFromDefeat,
    handleOpenDeckBuilderFromDefeat,
    resetBossBattle,
  };
};
